/* ═══════════════════════════════════════════════════════════════════════════
   Les armes SECONDAIRES — les formes que le moteur sait exécuter.

   Même frontière que `defs.ts` et `mods.ts`, et pour la même raison : le moteur
   connaît des FORMES, le contenu fournit des DONNÉES. Ajouter une arme
   secondaire est un fichier dans src/content/secondaries/ ; ajouter une
   nouvelle géométrie de tir est une modification de moteur. C'est cette
   frontière qui décide si le catalogue tiendra à cinquante armes.

   ── Pourquoi une secondaire, et ce qu'elle ne doit PAS être ────────────────
   La primaire est automatique (PLAN.md §6.3) : le joueur n'a pas à la piloter,
   toute son attention va à l'esquive. La secondaire réintroduit UNE décision,
   et une seule — « maintenant, ici ». Deux conséquences de conception qui sont
   des contraintes dures, pas des préférences :

     · elle ne remplace jamais la primaire. Son DPS moyen (dégâts totaux ÷
       recharge) reste sous celui de la primaire. Une secondaire qui domine
       transforme le jeu en « attendre la recharge », c'est-à-dire en attente.
     · elle répond à un BESOIN que la primaire ne couvre pas — une file
       d'ennemis alignés, un flanc découvert, une fenêtre de burst, une zone à
       interdire. Si elle ne fait que « plus de dégâts devant », elle ne crée
       aucune décision : on la déclenche dès qu'elle est prête, sans réfléchir,
       et le clic droit devient un tic nerveux.

   ── Pourquoi la recharge est bornée [2, 8] s ───────────────────────────────
   Sous 2 s, le joueur la spamme et elle redevient une primaire bis. Au-delà de
   8 s, il oublie qu'il l'a : une arme dont on ne se souvient pas est du contenu
   mort, exactement comme un mod jamais tiré. La borne est vérifiée au
   chargement (`validateSecondaries`) pour que la dérive se voie au build et non
   six mois plus tard en playtest.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Les géométries d'émission connues du moteur.
 *
 *  Chacune existe parce qu'elle résout un problème de terrain distinct, et
 *  aucune n'est une variante paramétrique d'une autre — sinon elle serait une
 *  DONNÉE, pas une forme. */
export const SecShape = {
  /** Colonne verticale de projectiles espacés, tirés au même instant.
   *
   *  C'est ainsi qu'on obtient de la PERFORATION sans toucher au moteur de
   *  collision : un projectile joueur meurt sur sa première cible (voir
   *  `collisionSystem`), mais le suivant de la colonne arrive quelques
   *  millisecondes plus tard sur la même ligne et poursuit la traversée. La
   *  perforation est donc ÉMERGENTE — zéro champ supplémentaire dans le pool,
   *  zéro branche dans la boucle de collision la plus chaude du jeu. */
  Lance: 0,
  /** Deux salves horizontales symétriques, une par flanc.
   *
   *  Le seul angle mort structurel du jeu : la primaire tire vers le haut, donc
   *  tout ce qui traverse à hauteur du vaisseau (Rôdeur et consorts) est
   *  intouchable sans se replacer. Cette forme achète du temps de replacement. */
  Flanc: 1,
  /** Rafale de volées très rapprochées, émises depuis la position COURANTE.
   *
   *  Le faisceau suit donc le vaisseau pendant sa durée : il se BALAIE. C'est ce
   *  qui en fait une arme de positionnement — les dégâts ne tombent que si l'on
   *  tient sa ligne, ce qui est une décision d'esquive, pas un clic. */
  Faisceau: 2,
  /** Dépôt de charges lentes qui montent vers les vagues à venir.
   *
   *  La seule forme dont l'effet est DIFFÉRÉ : on la joue en prévision de ce qui
   *  arrive, pas sur ce qu'on voit. Elle demande de lire la vague, pas l'écran. */
  Mine: 3,

  /* ═══ Les formes à CONDUITE ═══════════════════════════════════════════════
     Les quatre formes ci-dessus se distinguent par la géométrie de la volée :
     où partent les projectiles et dans quelle direction. Elles ont épuisé ce
     qu'on pouvait tirer de cette variable — au sixième fichier, l'éventail, le
     flanc, le faisceau et la mine sont quatre façons d'envoyer des points droit
     devant, et une septième disposition n'aurait rien posé de neuf au joueur.

     Les quatre suivantes se distinguent par ce que fait un projectile APRÈS
     être parti. C'est un changement de nature : elles n'ont pas été obtenues en
     réglant des données mais en apprenant au moteur des conduites qu'il ne
     savait pas exécuter (voir `PbMode` dans `BulletPool`). Chacune pose au
     joueur une question qu'aucune autre ne pose — c'est le seul critère qui
     justifie d'ouvrir le moteur plutôt que d'ajouter un fichier de contenu. */

  /** Projectiles qui infléchissent leur course vers une cible acquise devant
   *  eux.
   *
   *  La question posée : « puis-je tirer alors que je n'ai pas le temps de me
   *  placer ? » Toutes les autres armes exigent d'être alignées AVANT de tirer ;
   *  celle-ci est la seule qu'on déclenche en pleine esquive, en acceptant de
   *  ne pas viser. Ce qu'elle achète est de l'attention, pas des dégâts.
   *
   *  ⚠️ Le réglage du guidage est l'arme elle-même. Trop fort, il supprime la
   *  visée — donc le jeu, qui n'est plus qu'un clic. Trop faible, il ne se voit
   *  pas et le joueur croit à un tir droit qui rate. Le cône d'acquisition est
   *  ce qui garde une décision : le missile ne va QUE vers ce qui est déjà
   *  devant lui, et l'orienter reste le geste du joueur. */
  Traqueur: 4,

  /** Disques qui TRAVERSENT plusieurs ennemis, puis reviennent au vaisseau.
   *
   *  La question posée : « sont-ils alignés, et serai-je encore là au retour ? »
   *  La `Lance` simule déjà la perforation en empilant des projectiles, et c'est
   *  une bonne triche — mais elle traverse une file une seule fois, dans un seul
   *  sens. Le disque paie la même file deux fois s'il retrouve son point de
   *  départ, ce qui fait de la trajectoire du VAISSEAU pendant le vol une partie
   *  de l'arme. C'est la seule forme dont le rendement dépend de ce que le
   *  joueur fait APRÈS avoir tiré. */
  Scie: 5,

  /** Arc qui saute d'un ennemi au suivant, à courte portée.
   *
   *  La question posée : « sont-ils groupés ? » Exactement l'inverse de la
   *  `Scie`, qui demande un alignement : l'arc se moque de la géométrie et ne
   *  regarde que la DENSITÉ locale. Il est délibérément médiocre sur une cible
   *  isolée — un seul contact — et excellent sur un paquet, ce qui en fait la
   *  seule arme du catalogue qu'on renonce à déclencher faute de cible SUFFISANTE
   *  plutôt que faute de cible tout court. */
  Arc: 6,

  /** Faisceau ANCRÉ au vaisseau, qui persiste et blesse à cadence fixe.
   *
   *  La question posée : « puis-je suivre cette cible avec mon propre
   *  déplacement ? » C'est la seule arme sans temps de vol : partout ailleurs,
   *  0,4 à 0,7 s séparent le clic de l'impact, ce qui rend les autres armes
   *  aveugles à tout ce qui bouge vite ou brièvement — un Dard en piqué, une
   *  ouverture de boss. Le rayon touche à l'instant où on le pointe, et continue
   *  de toucher tant qu'on le tient.
   *
   *  ⚠️ Ne pas confondre avec `Faisceau`, qui n'est pas ancré : celui-ci tire
   *  une rafale de projectiles très rapides et le joueur PERD ses dégâts en
   *  bougeant. Le rayon les déplace avec lui. Les deux se ressemblent à l'écran
   *  et sont opposés à la main. */
  Rayon: 7,
} as const;

export type SecShapeId = (typeof SecShape)[keyof typeof SecShape];

export interface SecondaryDef {
  readonly id: string;
  readonly name: string;
  /** Une valeur de `SecShape`. */
  readonly shape: number;
  /** Recharge, en secondes. Bornée à [SECONDARY_CD_MIN, SECONDARY_CD_MAX]. */
  readonly cooldown: number;
  /** Volées successives d'un même déclenchement. 1 = arme ponctuelle.
   *
   *  ⚠️ C'est le seul mécanisme TEMPOREL du système, et il est volontairement
   *  orthogonal à la géométrie : n'importe quelle forme peut être tirée en
   *  rafale. Sans lui, « laser bref » aurait exigé une forme dédiée alors que
   *  c'est une donnée — dix-huit volées d'un projectile large. */
  readonly shots: number;
  /** Écart entre deux volées d'une rafale, en secondes. Ignoré si `shots` vaut
   *  1. Une valeur ≤ 0 est ramenée au pas de simulation à l'exécution : c'est
   *  un garde-fou contre une boucle d'émission qui ne se terminerait pas. */
  readonly shotEvery: number;
  /** Projectiles par volée. Pour `Flanc`, c'est le compte PAR CÔTÉ. */
  readonly count: number;
  /** Écartement géométrique, en unités monde. Son sens dépend de la forme :
   *  pas de la colonne (`Lance`), distance latérale d'apparition (`Flanc`),
   *  écart entre faisceaux (`Faisceau`), largeur totale du dépôt (`Mine`). */
  readonly spacing: number;
  /** Ouverture totale de l'étalement angulaire, en degrés. */
  readonly spreadDeg: number;
  readonly speed: number;
  /** Rayon VISUEL, mêmes conventions que le tir primaire. */
  readonly radius: number;
  /** Dégâts par projectile. */
  readonly damage: number;
  /** Texte du HUD.
   *
   *  ⚠️ Chiffré, exactement comme le texte d'une carte de mod : le joueur doit
   *  pouvoir comparer deux secondaires. « Salve perforante » ne dit rien ;
   *  « 6 × 100 dégâts alignés, 5 s » se compare. */
  readonly text: string;

  /* ── Données des formes à CONDUITE ────────────────────────────────────────
     Facultatives, et elles le resteront : les six armes balistiques n'en ont
     aucun usage et ne doivent pas porter trois zéros décoratifs qu'un lecteur
     essaierait d'interpréter. Chacune n'a de sens que pour certaines formes, et
     `validateSecondaries` refuse le chargement si l'une manque là où elle est
     nécessaire — une donnée facultative silencieusement absente donnerait un
     missile qui ne tourne pas, c'est-à-dire une arme livrée morte. */

  /** Vitesse de rotation du guidage, en degrés par seconde.
   *
   *  ⚠️ LE réglage de ce lot, et il se mesure — voir `tests/sim/arsenal.test.ts`,
   *  qui borne des deux côtés l'écart latéral rattrapable. Le deviner produit
   *  systématiquement l'une des deux armes ratées : celle qui vise à la place du
   *  joueur, et celle dont personne ne voit qu'elle guide. */
  readonly turnDeg?: number;

  /** Contacts qu'un projectile produit avant de mourir. Absent = 1.
   *
   *  ⚠️ Ce nombre entre DIRECTEMENT dans le calcul du DPS (`secondaryDps`), et
   *  c'est tout son intérêt : un disque qui traverse quatre ennemis inflige
   *  quatre fois ses dégâts. Sans lui, la perforation serait le moyen le plus
   *  simple de faire passer une arme quatre fois trop forte devant un garde-fou
   *  qui la trouverait irréprochable. */
  readonly hits?: number;

  /** Durée de la conduite, en secondes : vol aller d'un disque avant son
   *  demi-tour, persistance d'un rayon. */
  readonly duration?: number;
}

/** Intervalle entre deux impulsions d'un rayon ancré, en secondes.
 *
 *  Constante de moteur et non donnée de contenu, pour la même raison que
 *  `DIVE_APPROACH` dans `motionSystem` : ce n'est pas un curseur d'équilibrage
 *  mais la définition de ce qu'est un rayon. À 0,3 s on verrait des salves ; à
 *  un tick, chaque segment pousserait 120 événements par seconde et l'anneau
 *  d'événements — 512 entrées — saturerait à lui seul, faisant disparaître les
 *  explosions et les frôlements du reste de la partie. 0,1 s se lit comme
 *  continu et laisse le budget intact. */
export const BEAM_PULSE = 0.1;

/** Nombre d'impulsions d'un rayon de durée `d`. */
export function beamPulses(d: number): number {
  return Math.floor(d / BEAM_PULSE);
}

/* ⚠️ NOTE DE CONCEPTION — pourquoi un rayon a peu de segments, et gros.
 *
 *  Première version : douze segments fins de 26 u, espacés de 46. Elle passait
 *  le garde-fou avec 70 DPS nominaux et en délivrait 163 sur une grille dense
 *  au banc de mesure, soit deux fois et demie l'annonce — plus que TOUTE arme du
 *  catalogue, et 36 % au-dessus de la Lance qui est censée rester la référence.
 *
 *  La cause n'était pas le réglage mais la comptabilité. On ne comptait que les
 *  quatre segments qu'un même corps peut chevaucher, au motif — juste — que
 *  `secondaryDps` mesure du mono-cible. Sauf qu'un projectile ordinaire ne PEUT
 *  pas dépasser son nominal : il meurt sur sa première cible. Un rayon, lui, a
 *  douze segments qui frappent douze corps différents à chaque impulsion. Le
 *  seul mode d'emploi honnête est donc de tous les compter — c'est-à-dire de
 *  revenir à l'hypothèse « tout touche » que ce fichier applique partout
 *  ailleurs, et qui n'existe que pour être pessimiste au bon endroit.
 *
 *  Conséquence sur la forme de l'arme, et c'est une bonne nouvelle : pour rester
 *  fort à dégâts nominaux bornés, un rayon doit avoir PEU de segments, donc de
 *  GROS. Sept segments de 38 u au lieu de douze de 26 — plus large, plus lisible,
 *  et un nominal qui coïncide avec la mesure au lieu de la sous-estimer d'un
 *  facteur 2,5. La contrainte d'équilibrage a dicté la bonne géométrie. */

/** Le fragment d'index que la simulation attend du catalogue.
 *
 *  Déclaré ici plutôt que dans `defs.ts` pour la même raison que `ModDef` vit
 *  dans `mods.ts` : le système qui consomme la donnée est celui qui en décrit
 *  la forme. `DefIndex` l'étend (voir l'intégration). */
export interface SecondaryIndex {
  readonly secondaries: readonly SecondaryDef[];
  /** id textuel → index numérique. Les fichiers de contenu et l'interface se
   *  réfèrent au texte ; `PlayerState.secondary` stocke l'entier, parce qu'un
   *  index est hachable et sérialisable en un mot machine — une chaîne ne
   *  l'est pas, et l'empreinte de replay en dépend. */
  readonly secondaryByI: ReadonlyMap<string, number>;
}

/** Arme équipée au démarrage d'une run.
 *
 *  Résolue par identifiant, jamais par index 0 : l'index dépend de l'ordre
 *  alphabétique des fichiers ramassés par le glob, donc renommer un fichier
 *  changerait l'arme de départ sans que personne ne le remarque. */
export const DEFAULT_SECONDARY_ID = 'lance-perforante';

/** Vitesse maximale d'un tir joueur, en u/s (PLAN.md §6.4 : fourchette
 *  1 400-2 200). Distinct du plafond absolu de 1 500 u/s, qui ne concerne que
 *  les projectiles ennemis. */
export const PLAYER_SHOT_SPEED_MAX = 2200;

export const SECONDARY_CD_MIN = 2;
export const SECONDARY_CD_MAX = 8;

/** Contacts qu'un seul projectile de cette arme peut produire.
 *
 *  ⚠️ C'est ici que se referme le piège central des formes à conduite. Le DPS
 *  d'une arme balistique se lit dans sa définition : un projectile, un ennemi,
 *  ses dégâts. Un disque qui traverse quatre corps inflige quatre fois ses
 *  dégâts avec exactement les mêmes chiffres écrits, et un arc qui saute trois
 *  fois aussi. Compter les projectiles au lieu des CONTACTS laisserait donc
 *  entrer une arme quadruple sans qu'aucune ligne du garde-fou ne bouge —
 *  l'exact contraire de ce pour quoi il existe.
 *
 *  Pour un rayon, un « contact » est une impulsion : la cadence est fixe et la
 *  durée est écrite, donc le compte est déterminé, jamais déclaré. Une donnée
 *  qu'on peut déduire ne doit pas pouvoir mentir. */
export function secondaryContacts(def: SecondaryDef): number {
  if (def.shape === SecShape.Rayon) {
    const n = beamPulses(def.duration ?? 0);
    return n > 1 ? n : 1;
  }
  const h = def.hits ?? 1;
  return h > 1 ? h : 1;
}

/** DPS moyen d'une secondaire : dégâts totaux d'un déclenchement ÷ recharge.
 *
 *  Grandeur volontairement optimiste — elle suppose que TOUS les projectiles
 *  touchent et que tous leurs contacts trouvent un corps, ce qui n'arrive jamais
 *  pour une salve latérale ou un champ de mines. C'est exactement ce qu'on veut
 *  d'un garde-fou : s'il passe dans le cas le plus favorable, il passe partout. */
export function secondaryDps(def: SecondaryDef): number {
  return (
    (projectilesPerVolley(def) * def.shots * def.damage * secondaryContacts(def)) / def.cooldown
  );
}

/** Projectiles émis par une volée.
 *
 *  Un seul cas particulier, et il est ancien : `Flanc` émet des deux côtés, donc
 *  le compte réel est le double du déclaré. Les segments d'un rayon sont comptés
 *  comme n'importe quels projectiles — voir la note de conception plus haut, qui
 *  explique pourquoi les en dispenser avait laissé passer une arme deux fois et
 *  demie trop forte. */
function projectilesPerVolley(def: SecondaryDef): number {
  return def.shape === SecShape.Flanc ? def.count * 2 : def.count;
}

/** Le guidage réalise-t-il le DPS nominal de cette arme ? */
function isGuided(def: SecondaryDef): boolean {
  return def.shape === SecShape.Traqueur;
}

/** Part du DPS primaire qu'une secondaire a le droit d'atteindre EN NOMINAL.
 *
 *  ══ Le décalage nominal / effectif, et pourquoi un seul plafond ne suffit plus
 *
 *  `secondaryDps` suppose que tout touche. Cette hypothèse est fausse dans des
 *  proportions TRÈS différentes selon l'arme, et le plafond unique de 80 % ne
 *  tenait que parce que les six premières armes étaient fausses de la même
 *  façon : elles envoient des projectiles droits vers un endroit où le joueur
 *  espère qu'un ennemi se trouvera. Une bonne moitié se perd, et les 80 %
 *  nominaux valent en pratique 30 à 40 % réalisés. C'est ce coefficient
 *  implicite, jamais écrit nulle part, qui rend la valeur 0,8 jouable.
 *
 *  Un missile guidé casse cette hypothèse : il ne rate pas ce qu'il a acquis.
 *  Ses 80 % nominaux sont 80 % RÉELS, soit plus du double de ce que rend la
 *  meilleure arme balistique du catalogue. Il serait déclaré conforme par le
 *  garde-fou tout en étant la meilleure arme du jeu — précisément la panne que
 *  ce fichier existe pour rendre impossible.
 *
 *  On le tient donc à un plafond plus bas, et l'écart n'est pas choisi au
 *  jugé : `tests/sim/arsenal.test.ts` mesure, dans un monde qui tourne, les
 *  dégâts RÉELLEMENT infligés par chaque arme sur une même formation, et exige
 *  qu'aucune ne dépasse la référence balistique. Le plafond ci-dessous est ce
 *  que cette mesure valide ; il n'est pas la source de vérité, il en est le
 *  résumé exécutable au chargement.
 *
 *  La perforation, elle, ne demande aucun plafond particulier : elle multiplie
 *  le dégât nominal, donc `secondaryContacts` la fait entrer dans le calcul
 *  ordinaire. Guidage et perforation sont deux problèmes distincts et c'est
 *  pour ça qu'ils ont deux traitements distincts — le premier change la
 *  PROBABILITÉ de toucher, le second le NOMBRE de touches. */
export function secondaryDpsCap(def: SecondaryDef): number {
  return isGuided(def) ? 0.4 : 0.8;
}

/**
 * Contrôles de chargement. Retourne la liste des problèmes, vide si tout va
 * bien — même convention que `validate()` du registre, qui les agrège.
 *
 * Ce ne sont pas des tests de code mais des assertions de CONCEPTION, au même
 * titre que `checkFeelInvariants` : elles existent pour faire échouer le build
 * le jour où quelqu'un « remonte juste un peu » les dégâts d'une secondaire
 * pour la rendre satisfaisante, et fait basculer le jeu dans l'attente de
 * recharge.
 */
export function validateSecondaries(
  list: readonly SecondaryDef[],
  primaryDps: number,
): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();

  for (const d of list) {
    if (seen.has(d.id)) problems.push(`secondaire dupliquée : ${d.id}`);
    seen.add(d.id);

    /* ⚠️ Plafond de vitesse des tirs JOUEUR (PLAN.md §6.4).
       2 200 u/s, et non les 1 500 du plafond absolu : cette dernière valeur
       borne les projectiles ENNEMIS, pour laisser au joueur 0,80 s de traversée
       d'écran — trois fois son temps de réaction. Les tirs du joueur sont
       délibérément plus rapides, c'est la hiérarchie de lecture qui distingue
       « ce qui vient de moi » de « ce qui me tue ».
       Confondre les deux plafonds ferait condamner du contenu parfaitement
       conforme ; ne vérifier ni l'un ni l'autre laisse dériver le seul repère
       qui rend les deux flux distinguables. Le Faisceau bref était à 2 600. */
    if (d.speed > PLAYER_SHOT_SPEED_MAX) {
      problems.push(
        `${d.id} : ${d.speed} u/s au-delà du plafond joueur de ${PLAYER_SHOT_SPEED_MAX} u/s`,
      );
    }

    if (d.cooldown < SECONDARY_CD_MIN || d.cooldown > SECONDARY_CD_MAX) {
      problems.push(
        `${d.id} : recharge ${d.cooldown} s hors de [${SECONDARY_CD_MIN}, ${SECONDARY_CD_MAX}] s`,
      );
    }
    if (d.shots < 1) problems.push(`${d.id} : shots doit valoir au moins 1`);
    if (d.shots > 1 && d.shotEvery <= 0) {
      problems.push(`${d.id} : une rafale de ${d.shots} volées exige shotEvery > 0`);
    }
    if (d.count < 1) problems.push(`${d.id} : count doit valoir au moins 1`);
    if (d.damage <= 0) problems.push(`${d.id} : damage doit être > 0`);
    if (d.radius <= 0) problems.push(`${d.id} : radius doit être > 0`);

    /* ── Cohérence des formes à conduite ────────────────────────────────
       Une donnée manquante ne produit pas une arme un peu moins bonne : elle
       produit une arme MORTE, et silencieusement. Un disque sans budget de
       perforation est une balle lente ; un rayon sans durée n'apparaît pas ;
       un missile sans rotation vole droit. Aucun de ces trois cas ne lève
       d'erreur à l'exécution — le joueur constate seulement que l'arme qu'il
       vient de débloquer « ne fait rien ». */
    if (d.shape === SecShape.Traqueur && !(d.turnDeg !== undefined && d.turnDeg > 0)) {
      problems.push(`${d.id} : un traqueur exige turnDeg > 0, sinon il vole droit`);
    }
    if (d.shape === SecShape.Scie) {
      if (!(d.turnDeg !== undefined && d.turnDeg > 0)) {
        problems.push(`${d.id} : une scie exige turnDeg > 0 pour revenir`);
      }
      if (!(d.duration !== undefined && d.duration > 0)) {
        problems.push(`${d.id} : une scie exige duration > 0 (durée du vol aller)`);
      }
    }
    if (d.shape === SecShape.Arc && !(d.turnDeg !== undefined && d.turnDeg > 0)) {
      problems.push(`${d.id} : un arc exige turnDeg > 0 pour sauter`);
    }
    if ((d.shape === SecShape.Scie || d.shape === SecShape.Arc) && (d.hits ?? 1) < 2) {
      problems.push(
        `${d.id} : hits ≥ 2 attendu — une arme qui traverse ou qui saute une ` +
          `seule fois est un projectile ordinaire sous un autre nom`,
      );
    }
    if (d.shape === SecShape.Rayon) {
      if (!(d.duration !== undefined && d.duration > 0)) {
        problems.push(`${d.id} : un rayon exige duration > 0`);
      } else if (d.duration >= d.cooldown) {
        // Un rayon plus long que sa recharge reste allumé en permanence : la
        // décision disparaît, et avec elle la raison d'être d'une secondaire.
        problems.push(
          `${d.id} : durée ${d.duration} s ≥ recharge ${d.cooldown} s — le rayon ne s'éteindrait jamais`,
        );
      }
    }
    // Le budget de contacts vit dans un Uint8Array : au-delà, il déborde
    // silencieusement et l'arme devient soit inoffensive, soit éternelle.
    if ((d.hits ?? 1) > 255) problems.push(`${d.id} : hits doit tenir dans un octet`);

    /* ⚠️ LA règle du système. Une secondaire qui bat la primaire en DPS moyen
       n'est plus tactique : elle devient l'arme principale, et la primaire — le
       seul tir que le joueur n'a pas à gérer — devient un bruit de fond.

       Le plafond dépend désormais de la façon dont l'arme RÉALISE son nominal
       (voir `secondaryDpsCap`) : une arme qui ne rate pas doit annoncer moins
       qu'une arme qui rate la moitié de ses projectiles, faute de quoi le même
       chiffre décrirait deux puissances du simple au double. */
    const cap = secondaryDpsCap(d);
    const ratio = secondaryDps(d) / primaryDps;
    if (ratio > cap) {
      problems.push(
        `${d.id} : ${Math.round(ratio * 100)} % du DPS primaire (max ${Math.round(cap * 100)} %) — ` +
          `elle remplacerait la primaire au lieu de la compléter`,
      );
    }
  }

  return problems;
}
